/**
 * Customer-facing guidance for the product attribute sections of the Edit product dialog (PLT-7430):
 * the (i) tooltip on each section, and the warning line a section shows when its attributes break a
 * rule that switches them off. The chat's product-set read carries the same text, so the chat and
 * the editor say the same thing about where an attribute applies.
 *
 * Each tooltip says where the attribute applies, what it changes and what it needs in order to
 * count. The rules behind the wording live in the DMA trigger filters
 * (`apply-ipra-trigger-filters/attribute-filters/`) and the net impact mixin expression
 * (`ipr-to-logical-expressions.ts`); a change there should be checked against this copy.
 */
import { IPRAttributes, OfferingType, Tangibility } from '.';
import { INDUSTRY_LIMIT } from './industry-limit';

export type AttributeSection =
  | 'recyclability'
  | 'energySources'
  | 'certificates'
  | 'targetIndustry'
  | 'productType'
  | 'businessModel'
  | 'specialEndUses'
  | 'absentRawMaterials'
  | 'specialAttributes';

export const ATTRIBUTE_GUIDANCE: Record<AttributeSection, string> = {
  recyclability:
    'Use for physical goods designed to be reusable, repairable, modular, mono-material, biodegradable, ' +
    'refurbished or made from recycled materials, such as materials, packaging, equipment, furniture and ' +
    'building products. It describes the product itself, not its packaging, and counts when the product is ' +
    'marked Physical under Product type.',
  energySources:
    'Use when the product is made with one clearly documented energy source, such as green steel or chemicals ' +
    'made with bioenergy. Set one source per product. Renewables counts for energy-intensive goods: metals, ' +
    'chemicals, fertilisers, cement, glass, paper, plastics, textiles, batteries, chips and data centres. It ' +
    'doesn’t apply to professional services, education or care, to company-wide green power deals, or when ' +
    'the product type already says it, such as electric cars.',
  certificates:
    'Use for certified product lines in wood and paper (FSC, PEFC), seafood (MSC, ASC), food and plants ' +
    '(Fairtrade, Rainforest Alliance), textiles, clothing and leather (GOTS, OEKO-TEX, LWG, RWS, RDS, ' +
    'Fairtrade), cosmetics (COSMOS), hygiene, cleaning and pet products (PETA) and buildings (LEED, BREEAM). ' +
    'Set the one certificate that matches the product’s sector. Certificates held by the company rather than ' +
    'the product, and packaging certificates, don’t apply.',
  targetIndustry:
    'Use for business products made for specific industries, such as hospital beds for healthcare. It changes ' +
    'how the product’s use is assessed across net impact, SDG, EU Taxonomy and DMA. ' +
    `Add up to ${INDUSTRY_LIMIT} industries: with ${INDUSTRY_LIMIT + 1} or more, the double materiality ` +
    'assessment ignores all of them. ' +
    'Leave it empty for consumer products, for products that serve many industries, when the product type ' +
    'already implies the industry (such as medicines), and for industries where the company simply has ' +
    'customers.',
  productType:
    'Says whether the company makes the product, and whether it’s a physical good, a digital product or a ' +
    'service. Set it for every product, because Recyclability and Business model use it to decide whether ' +
    'they apply.',
  businessModel:
    'Use for products the company resells, distributes, rents, installs, repairs or licenses without making ' +
    'them. It moves the product’s environmental and workforce impacts from the company’s own operations to ' +
    'its value chain. In the double materiality assessment, retail, wholesale and distribution need Physical ' +
    'under Product type, and installation, repair, licensing, rental and leasing need Service. Leave it empty ' +
    'for products the company makes itself, unless it also sells them through its own retail or wholesale.',
  specialEndUses:
    'Use when the product’s main purpose is teaching and learning, or when it’s designed for people with ' +
    'disabilities, humanitarian aid or language minority groups. A product these groups can use but that ' +
    'isn’t made for them, such as a laptop used in schools, doesn’t qualify.',
  absentRawMaterials:
    'Use for personal care, cosmetics and food products sold as free of a specific substance, such as ' +
    'paraben-free. It doesn’t apply to services, or to products that wouldn’t contain the material anyway.',
  specialAttributes:
    'Use “In developing countries” when most of the product’s use is there: it gives more weight to food, ' +
    'housing, water and infrastructure impacts. “Without animal testing” is for cosmetics, personal care and ' +
    'cleaning products, and “Follows journalistic ethics” is for news media.',
};

/** The Advanced attributes tab's own tooltip. */
export const ADVANCED_ATTRIBUTES_TAB_GUIDANCE =
  'For products sold, delivered or used in a particular way: resold, distributed, installed or repaired ' +
  'rather than made by the company, or made for a special purpose or group. Start with Product type, ' +
  'because other attributes depend on it.';

type BusinessModelFlag = keyof Pick<
  IPRAttributes,
  | 'IsPhysicalRetail'
  | 'IsOnlineRetail'
  | 'IsWholesale'
  | 'IsDistributionOf'
  | 'IsInstallationOf'
  | 'IsRepairOrMaintenanceOf'
  | 'IsLicensingOf'
  | 'IsAssemblyOf'
  | 'IsRental'
  | 'IsLeasing'
>;

const BUSINESS_MODEL_NAMES: Record<BusinessModelFlag, string> = {
  IsPhysicalRetail: 'Physical retail',
  IsOnlineRetail: 'Online retail',
  IsWholesale: 'Wholesale',
  IsDistributionOf: 'Distribution',
  IsInstallationOf: 'Installation',
  IsRepairOrMaintenanceOf: 'Repair or maintenance',
  IsLicensingOf: 'Licensing',
  IsAssemblyOf: 'Assembly',
  IsRental: 'Rental',
  IsLeasing: 'Leasing',
};

// Each list mirrors the `applyCondition` of the flags' DMA filters (`attribute-filters/business-model/`).
// An unset manufacturing status passes their `!isManufactured` check, so only an explicit
// "Manufactured by the company" switches the last group off.
const NEEDS_PHYSICAL: BusinessModelFlag[] = ['IsPhysicalRetail', 'IsOnlineRetail', 'IsWholesale', 'IsDistributionOf'];
const NEEDS_SERVICE: BusinessModelFlag[] = [
  'IsInstallationOf',
  'IsRepairOrMaintenanceOf',
  'IsLicensingOf',
  'IsRental',
  'IsLeasing',
];
const NOT_IF_MANUFACTURED: BusinessModelFlag[] = [
  'IsDistributionOf',
  'IsInstallationOf',
  'IsRepairOrMaintenanceOf',
  'IsLicensingOf',
  'IsAssemblyOf',
];

/**
 * The warning line each attribute section shows when the product's attributes break a rule that
 * switches them off, keyed by section; a section with nothing wrong has no entry. Pass the
 * attributes the results will use: the product's own merged with what it inherits.
 *
 * The rules are the DMA trigger filters', so a warning names the double materiality assessment
 * wherever another framework still reads the attribute: net impact uses every industry and the
 * FSC/PEFC certificates, and EU Taxonomy and the PAI GHG estimate treat a retail product as retail
 * whatever its tangibility. Energy sources and recyclability feed nothing but DMA, so theirs don't.
 *
 * Only rules that the attributes alone decide are checked here. Whether an attribute applies to the
 * product's matched taxonomy product (e.g. Renewables only for energy-intensive goods) is not known
 * to the editor.
 */
export function getAttributeWarnings(ipr: Partial<IPRAttributes>): Partial<Record<AttributeSection, string>> {
  const warnings: Partial<Record<AttributeSection, string>> = {};

  const industries = ipr.TargetIndustry?.length ?? 0;
  if (industries > INDUSTRY_LIMIT) {
    warnings.targetIndustry = `With ${industries} industries, none of them counts in the double materiality assessment. Keep up to ${INDUSTRY_LIMIT}.`;
  }
  const certificates = ipr.Certifications?.length ?? 0;
  if (certificates > 1) {
    warnings.certificates = `With ${certificates} certificates, none of them counts in the double materiality assessment. Keep the one that matches the product’s sector.`;
  }
  const energySources = ipr.EnergySourcesUsed?.length ?? 0;
  if (energySources > 1) {
    warnings.energySources = `With ${energySources} energy sources, none of them counts. Keep the one the product is made with.`;
  }
  if ((ipr.Recyclability?.length ?? 0) > 0 && ipr.Tangibility !== Tangibility.Physical) {
    warnings.recyclability =
      'Recyclability counts only for physical goods. Set Tangibility to Physical under Product type, on the ' +
      'Advanced attributes tab.';
  }

  const selected = (flags: BusinessModelFlag[]) => flags.filter(flag => ipr[flag] === true);
  const businessModel: string[] = [];
  const withoutPhysical = ipr.Tangibility === Tangibility.Physical ? [] : selected(NEEDS_PHYSICAL);
  if (withoutPhysical.length > 0) {
    businessModel.push(
      `In the double materiality assessment, ${listOf(withoutPhysical)} ${countVerb(withoutPhysical)} only for physical goods. Set Tangibility to Physical under Product type.`,
    );
  }
  const withoutService = ipr.OfferingType === OfferingType.Service ? [] : selected(NEEDS_SERVICE);
  if (withoutService.length > 0) {
    businessModel.push(
      `In the double materiality assessment, ${listOf(withoutService)} ${countVerb(withoutService)} only as a service. Set Offering type to Service under Product type.`,
    );
  }
  const manufactured = ipr.isManufactured === true ? selected(NOT_IF_MANUFACTURED) : [];
  if (manufactured.length > 0) {
    businessModel.push(
      `In the double materiality assessment, ${listOf(manufactured)} ${countVerb(manufactured)} only for products the company doesn’t make, and this one is marked Manufactured by the company.`,
    );
  }
  if (businessModel.length > 0) {
    warnings.businessModel = businessModel.join(' ');
  }

  return warnings;
}

/** "physical retail, wholesale and distribution": the names as one phrase, mid-sentence. */
function listOf(flags: BusinessModelFlag[]): string {
  const names = flags.map(flag => BUSINESS_MODEL_NAMES[flag].toLowerCase());
  if (names.length === 1) return names[0];
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

function countVerb(flags: BusinessModelFlag[]): string {
  return flags.length === 1 ? 'counts' : 'count';
}
